import { useState } from 'react';
import { Mail, Phone, ShieldCheck, Trash2, UserPlus } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { formatTimeAgo } from '@/lib/format';
import { getNameInitials, telHref } from '@/lib/utils';
import type { InstituteAdmin } from '@/types/institute';
import { institutesApi } from '../api/institutes.api';
import { AddAdminDialog } from './AddAdminDialog';

export interface InstituteAdminsPanelProps {
  instituteUuid: string;
  instituteName: string;
  /** Only a SUPER_ADMIN changes who runs an institute; support may look. */
  canManage: boolean;
}

/**
 * Everyone who administers an institute, and — for a SUPER_ADMIN — adding,
 * re-inviting and removing them.
 *
 * Managed here and nowhere else: the institute's own Staff screen refuses to
 * touch an administrator account, so a school cannot appoint or remove its
 * own owners. The primary administrator, on the institute's contact address,
 * is the one the console's other actions reach, so it is not removable here.
 */
export function InstituteAdminsPanel({ instituteUuid, instituteName, canManage }: InstituteAdminsPanelProps) {
  const { data: admins, isLoading, error, refetch } = useApiQuery(
    () => institutesApi.listAdmins(instituteUuid),
    [instituteUuid],
  );

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [removing, setRemoving] = useState<InstituteAdmin | null>(null);
  const [busyUuid, setBusyUuid] = useState<string | null>(null);

  const isOnlyAdmin = (admins?.length ?? 0) <= 1;

  function reasonFor(caught: unknown): string {
    return caught instanceof ApiRequestError ? caught.message : 'Please try again.';
  }

  async function resendInvite(admin: InstituteAdmin) {
    setBusyUuid(admin.uuid);
    try {
      await institutesApi.resendAdminInvite(instituteUuid, admin.uuid);
      toast.success('Invite sent', `A new set-password link is on its way to ${admin.email}.`);
    } catch (caught) {
      toast.error(`Could not resend ${admin.name}'s invite`, reasonFor(caught));
    } finally {
      setBusyUuid(null);
    }
  }

  async function confirmRemove() {
    if (!removing) return;
    setBusyUuid(removing.uuid);
    try {
      await institutesApi.removeAdmin(instituteUuid, removing.uuid);
      toast.success(`${removing.name} is no longer an administrator`, 'They have been signed out everywhere.');
      setRemoving(null);
      refetch();
    } catch (caught) {
      toast.error(`Could not remove ${removing.name}`, reasonFor(caught));
    } finally {
      setBusyUuid(null);
    }
  }

  function handleAdded(admin: InstituteAdmin) {
    setIsAddOpen(false);
    toast.success(`${admin.name} is now an administrator`, `A link to set their password was sent to ${admin.email}.`);
    refetch();
  }

  return (
    <section className="card p-4 sm:p-5" aria-labelledby="institute-admins-heading">
      <header className="mb-4 flex flex-col gap-3 border-b border-line pb-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5">
          <span
            className="grid h-7 w-7 shrink-0 place-items-center rounded-field bg-accent-soft text-accent"
            aria-hidden
          >
            <ShieldCheck className="h-4 w-4" />
          </span>
          <div>
            <h2 id="institute-admins-heading" className="text-base font-[650] text-ink">
              Administrators
            </h2>
            <p className="text-sm text-ink-2">Full access to {instituteName}, including roles and every setting.</p>
          </div>
        </div>
        {canManage && (
          <Button leftIcon={<UserPlus className="h-4 w-4" />} onClick={() => setIsAddOpen(true)}>
            Add administrator
          </Button>
        )}
      </header>

      {error ? (
        <QueryError title="Could not load the administrators" error={error} onRetry={refetch} />
      ) : isLoading || !admins ? (
        <SkeletonLines count={4} />
      ) : admins.length === 0 ? (
        <EmptyState
          icon={<ShieldCheck className="h-5 w-5" />}
          title="No administrator account yet"
          description="Nobody can sign in to run this institute. Add an administrator to send them a set-password link."
          action={
            canManage ? (
              <Button leftIcon={<UserPlus className="h-4 w-4" />} onClick={() => setIsAddOpen(true)}>
                Add administrator
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ul className="flex flex-col divide-y divide-line">
          {admins.map((admin) => (
            <li
              key={admin.uuid}
              className="flex flex-col gap-3 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex min-w-0 items-start gap-3">
                <span
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-surface-2 text-sm font-bold text-ink-2"
                  aria-hidden
                >
                  {getNameInitials(admin.name)}
                </span>
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-[650] text-ink">
                    {admin.name}
                    {admin.isPrimary && <Badge tone="accent">Primary</Badge>}
                    {admin.invitePending ? (
                      <Badge tone="warning">Invite pending</Badge>
                    ) : admin.status !== 'ACTIVE' ? (
                      <Badge tone="critical">{admin.status === 'SUSPENDED' ? 'Suspended' : 'Inactive'}</Badge>
                    ) : null}
                  </p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-ink-2">
                    <a
                      href={`mailto:${admin.email}`}
                      className="inline-flex min-w-0 items-center gap-1 truncate rounded hover:text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
                    >
                      <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      {admin.email}
                    </a>
                    {admin.phone && (
                      <a
                        href={telHref(admin.phone)}
                        className="inline-flex items-center gap-1 rounded font-mono hover:text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
                      >
                        <Phone className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        {admin.phone}
                      </a>
                    )}
                  </p>
                  <p className="mt-0.5 text-xs text-ink-3">
                    {admin.lastLoginAt
                      ? `Last signed in ${formatTimeAgo(admin.lastLoginAt)}`
                      : 'Has not signed in yet'}
                  </p>
                </div>
              </div>

              {canManage && (
                <div className="flex shrink-0 items-center gap-2 sm:pl-4">
                  {admin.invitePending && (
                    <Button
                      variant="secondary"
                      size="sm"
                      isLoading={busyUuid === admin.uuid && !removing}
                      onClick={() => resendInvite(admin)}
                      aria-label={`Resend ${admin.name}'s invite`}
                    >
                      Resend invite
                    </Button>
                  )}
                  {/* Said on the button rather than hidden: a missing Remove
                      would leave support wondering whether it exists at all. */}
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                    disabled={admin.isPrimary || isOnlyAdmin}
                    title={
                      admin.isPrimary
                        ? 'The primary administrator is changed by editing the institute'
                        : isOnlyAdmin
                          ? 'An institute needs at least one administrator'
                          : undefined
                    }
                    onClick={() => setRemoving(admin)}
                    aria-label={`Remove ${admin.name} as an administrator`}
                  >
                    Remove
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && admins && admins.some((admin) => admin.isPrimary) && (
        <p className="mt-4 border-t border-line pt-3 text-xs text-ink-3">
          The primary administrator is the one on the institute&apos;s contact email. To make someone
          else primary, edit the institute and change its administrator email.
        </p>
      )}

      <AddAdminDialog
        instituteUuid={instituteUuid}
        instituteName={instituteName}
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onAdded={handleAdded}
      />

      <ConfirmDialog
        isOpen={Boolean(removing)}
        tone="danger"
        title={`Remove ${removing?.name ?? 'this administrator'}?`}
        description={`They are signed out of ${instituteName} at once and can no longer sign in. Their account is kept for the audit trail, and they can be added again later.`}
        confirmLabel="Remove administrator"
        isLoading={Boolean(removing) && busyUuid === removing?.uuid}
        onConfirm={confirmRemove}
        onCancel={() => setRemoving(null)}
      />
    </section>
  );
}
